/** @module store/custom_events */

import * as apiCustomEvents from '@/api/custom_events'
import { formatTimestampToLocal } from '@/helpers/view_helpers'

const state = () => ({
  emsToCustomEvents: {}
})

const mutations = {
  ADD_CUSTOM_EVENT: (state, { customEvent }) => {
    if (!(customEvent instanceof CustomEvent)) return

    state.emsToCustomEvents[customEvent.energyManagementSystemId] ||= {}

    state.emsToCustomEvents[customEvent.energyManagementSystemId][customEvent.id] = customEvent
  },
  SET_CUSTOM_EVENTS_FOR_EMS: (state, { emsId, customEvents }) => {
    state.emsToCustomEvents[emsId] = customEvents
      .filter((e) => e instanceof CustomEvent)
      .reduce((prev, e) => ({ ...prev, [e.id]: e }), {})
  },
  DELETE_CUSTOM_EVENT: (state, { emsId, id }) => {
    delete state.emsToCustomEvents[emsId]?.[id]

    if (Object.entries(state.emsToCustomEvents[emsId]).length) return

    delete state.emsToCustomEvents[emsId]
  }
}

const getters = {
  getCustomEvent: (state) => (emsId, customEventId) => state.emsToCustomEvents[emsId]?.[customEventId],
  getCustomEventsForEms: (state) => (emsId) =>
    Object.values(state.emsToCustomEvents[emsId] || [])?.sort((a, b) => a.t - b.t),
  getCustomEventsCsvInfoForEms: (_, getters) => (emsId, locale) =>
    getters.getCustomEventsForEms(emsId).map((customEvent) => customEvent.toCsvObject(locale))
}

const actions = {
  createCustomEvent: ({ commit }, { ctxt, emsId, t, tEnd, label, description }) =>
    apiCustomEvents.createCustomEvent({ ctxt, emsId, t, tEnd, label, description }).then((msg) => {
      const customEvent = resourceToCustomEvent(msg?.payload?.data)

      commit('ADD_CUSTOM_EVENT', { customEvent })
    }),
  deleteCustomEvent: ({ commit }, { ctxt, emsId, id }) =>
    apiCustomEvents.deleteCustomEvent({ ctxt, emsId, id }).then(() => {
      commit('DELETE_CUSTOM_EVENT', { emsId, id })
    }),
  getCustomEventsForEms: ({ commit }, { ctxt, emsId }) =>
    apiCustomEvents.getCustomEventsForEms({ ctxt, emsId }).then((msg) => {
      const customEvents = msg?.payload?.data?.map((resource) => resourceToCustomEvent(resource))

      commit('SET_CUSTOM_EVENTS_FOR_EMS', { emsId, customEvents })
    })
}

const resourceToCustomEvent = (resource) =>
  new CustomEvent({
    id: resource.id,
    energyManagementSystemId: resource.relationships.energyManagementSystem.data.id,
    t: resource.attributes.t,
    tEnd: resource.attributes.tEnd,
    label: resource.attributes.label,
    description: resource.attributes.description
  })

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}

/**
 * A CustomEvent data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.id is the unique identifier of the Event
 * @param {integer} params.energyManagementSystemId referenced EMS
 * @param {string|moment} params.t Event (start)time
 * @param {string|moment} params.tEnd (optional) Event endtime
 * @param {string} params.label the label text of the event
 * @param {string} params.description (optional) description text of the event
 *
 */
export class CustomEvent {
  constructor({ id, energyManagementSystemId, t, tEnd, label, description }) {
    if (!id) throw new TypeError('An Id has to be present')
    if (!energyManagementSystemId) throw new TypeError('An EMS-ID has to be present')

    this.id = id
    this.energyManagementSystemId = energyManagementSystemId
    this.t = moment(t)
    this.tEnd = tEnd ? moment(tEnd) : null
    this.label = label
    this.description = description
  }

  getFormattedPeriod(locale) {
    if (this.tEnd) return `${this.getFormattedT(locale)} - ${this.getFormattedTEnd(locale)}`

    return this.getFormattedT(locale)
  }

  getFormattedT(locale) {
    return formatTimestampToLocal(this.t, { locale })
  }

  getFormattedTEnd(locale) {
    return this.tEnd ? formatTimestampToLocal(this.tEnd, { locale }) : null
  }

  toCsvObject(locale) {
    return {
      id: this.id,
      t: this.getFormattedPeriod(locale),
      label: this.label,
      description: this.description
    }
  }
}
